/** Top road section of the LUDOKA game board - Celeste skin */

import type { RoadSectionProps } from '../../constants/types';
import { renderMultiplePositions } from '../basics/utils/utils';
import { TOP_CENTER_SQUARE_BOUNDS as CENTER_SQUARE_BOUNDS } from '../basics/constants/center-square-bounds';
import type { BoardPosition } from '../../constants/positions';

/**
 * Render order for top section (Blue)
 * Ordered top to bottom for proper 3D overlap
 */
const RENDER_ORDER: BoardPosition[] = [
  'BS1',
  'BC1',
  'BF1',
  'BS2',
  'BC2',
  'BF2',
  'BS3',
  'BC3',
  'BF3',
  'BS4',
  'BC4',
  'BF4',
  'BS5',
  'BC5',
  'BF5',
  'BS6',
  'BC6',
  'BF6',
  'BFINAL'
];

export default function CelesteTopRoadSection({
  viewBox = '0 0 268 616',
  className = '',
  pawns = [],
  animatingPawnIds,
  kickingPawnIds,
  rollbackPawnIds,
  onPawnClick,
  clickablePawnIds,
  pawnComponents
}: RoadSectionProps) {
  return (
    <svg
      viewBox={viewBox}
      className={className}
      overflow="visible"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Top road section with blue final stretch"
    >
      {/* Row 6: Blue gradient center and sky-blue side squares */}
      <path
        d="M95.5801 403.61V480.45H172.42V403.61H95.5801Z"
        fill="url(#ctt_paint0)"
        stroke="black"
        strokeMiterlimit="10"
      />
      <path
        d="M166.09 466.05V418C166.09 413.549 162.482 409.94 158.03 409.94H109.98C105.529 409.94 101.92 413.549 101.92 418V466.05C101.92 470.501 105.529 474.11 109.98 474.11H158.03C162.482 474.11 166.09 470.501 166.09 466.05Z"
        fill="white"
      />
      <path
        d="M172.42 480.44H249.26V403.6H172.42V480.44Z"
        fill="#CEECF4"
        stroke="black"
        strokeMiterlimit="10"
      />
      <path
        d="M18.7401 480.44H95.5801L95.5801 403.6H18.7401L18.7401 480.44Z"
        fill="#CEECF4"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Row 5: Dark gradient center and darker sky-blue side squares */}
      <path
        d="M95.5801 326.77V403.61H172.42V326.77H95.5801Z"
        fill="url(#ctt_paint1)"
        stroke="black"
        strokeMiterlimit="10"
      />
      <path
        d="M166.09 389.21V341.16C166.09 336.709 162.482 333.1 158.03 333.1H109.98C105.529 333.1 101.92 336.709 101.92 341.16V389.21C101.92 393.662 105.529 397.27 109.98 397.27H158.03C162.482 397.27 166.09 393.662 166.09 389.21Z"
        fill="white"
      />
      <path
        d="M172.42 403.6H249.26V326.76H172.42V403.6Z"
        fill="#A7DFFC"
        stroke="black"
        strokeMiterlimit="10"
      />
      <path
        d="M18.7401 403.6H95.5801L95.5801 326.76H18.7401L18.7401 403.6Z"
        fill="#A7DFFC"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Row 4: Blue gradient center and sky-blue side squares */}
      <path
        d="M95.5703 249.93V326.77H172.41V249.93H95.5703Z"
        fill="url(#ctt_paint2)"
        stroke="black"
        strokeMiterlimit="10"
      />
      <path
        d="M166.09 312.37V264.32C166.09 259.869 162.482 256.26 158.03 256.26H109.98C105.529 256.26 101.92 259.869 101.92 264.32V312.37C101.92 316.821 105.529 320.43 109.98 320.43H158.03C162.482 320.43 166.09 316.821 166.09 312.37Z"
        fill="white"
      />
      <path
        d="M172.42 326.77H249.26V249.93H172.42V326.77Z"
        fill="#CEECF4"
        stroke="black"
        strokeMiterlimit="10"
      />
      <path
        d="M18.7401 326.77H95.5801L95.5801 249.93H18.7401L18.7401 326.77Z"
        fill="#CEECF4"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Row 3: Dark gradient center and darker sky-blue side squares */}
      <path
        d="M95.5801 173.08V249.92H172.42V173.08H95.5801Z"
        fill="url(#ctt_paint3)"
        stroke="black"
        strokeMiterlimit="10"
      />
      <path
        d="M166.08 235.52V187.47C166.08 183.019 162.471 179.41 158.02 179.41H109.97C105.519 179.41 101.91 183.019 101.91 187.47V235.52C101.91 239.971 105.519 243.58 109.97 243.58H158.02C162.471 243.58 166.08 239.971 166.08 235.52Z"
        fill="white"
      />
      <path
        d="M172.42 249.93H249.26V173.09H172.42V249.93Z"
        fill="#A7DFFC"
        stroke="black"
        strokeMiterlimit="10"
      />
      <path
        d="M18.7401 249.93H95.5801L95.5801 173.09H18.7401L18.7401 249.93Z"
        fill="#A7DFFC"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Row 2: Blue gradient center, dark gradient right, sky-blue left */}
      <path
        d="M95.5801 96.2401V173.08H172.42V96.2401H95.5801Z"
        fill="url(#ctt_paint4)"
        stroke="black"
        strokeMiterlimit="10"
      />
      <path
        d="M166.08 158.68V110.63C166.08 106.179 162.471 102.57 158.02 102.57H109.97C105.519 102.57 101.91 106.179 101.91 110.63V158.68C101.91 163.131 105.519 166.74 109.97 166.74H158.02C162.471 166.74 166.08 163.131 166.08 158.68Z"
        fill="white"
      />
      <path
        d="M172.42 173.09H249.26V96.2501L172.42 96.2501V173.09Z"
        fill="url(#ctt_paint5)"
        stroke="black"
        strokeMiterlimit="10"
      />
      <path
        d="M18.7401 173.09H95.5801L95.5801 96.2501L18.7401 96.2501L18.7401 173.09Z"
        fill="#CEECF4"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Row 1: Top rounded corners */}
      <path
        d="M249.26 96.25L172.42 96.25V19.41L239.26 19.41C244.78 19.41 249.26 23.89 249.26 29.41V96.25Z"
        fill="#A7DFFC"
        stroke="black"
        strokeMiterlimit="10"
      />
      <path
        d="M95.5804 96.25L172.42 96.25V19.41L95.5804 19.41L95.5804 96.25Z"
        fill="#CEECF4"
        stroke="black"
        strokeMiterlimit="10"
      />
      <path
        d="M95.5801 96.25L18.7401 96.25L18.7401 29.41C18.7401 23.89 23.2201 19.41 28.7401 19.41L95.5801 19.41L95.5801 96.25Z"
        fill="#A7DFFC"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Star decoration - inline (shadow layer + white top layer) */}
      {/* Shadow path uses absolute coordinates from SVG source */}
      <path
        d="M65.53 199.87L58.43 188.2C57.74 187.07 56.1 187.1 55.45 188.25L48.7 200.13L35.4 203.28C34.12 203.58 33.63 205.16 34.52 206.13L43.73 216.22L42.62 229.84C42.51 231.16 43.86 232.1 45.06 231.56L57.5 225.91L70.11 231.18C71.33 231.69 72.64 230.7 72.5 229.39L70.98 215.81L79.89 205.45C80.75 204.45 80.22 202.89 78.93 202.63L65.54 199.88L65.53 199.87Z"
        fill="#303030"
      />
      {/* White top layer */}
      <path
        d="M65.4802 198.57L58.3802 186.9C57.6902 185.77 56.0502 185.8 55.4002 186.95L48.6502 198.83L35.3502 201.98C34.0702 202.28 33.5802 203.86 34.4702 204.83L43.6802 214.92L42.5702 228.54C42.4602 229.86 43.8102 230.8 45.0102 230.26L57.4502 224.61L70.0602 229.88C71.2802 230.39 72.5902 229.4 72.4502 228.09L70.9302 214.51L79.8402 204.15C80.7002 203.15 80.1702 201.59 78.8802 201.33L65.4902 198.58L65.4802 198.57Z"
        fill="white"
      />

      {/* White center path square in right column row 2 */}
      <path
        d="M242.92 158.68V110.63C242.92 106.179 239.312 102.57 234.86 102.57H186.81C182.359 102.57 178.75 106.179 178.75 110.63V158.68C178.75 163.131 182.359 166.74 186.81 166.74H234.86C239.312 166.74 242.92 163.131 242.92 158.68Z"
        fill="white"
      />

      {/* Blue gradient triangle at bottom */}
      <path
        d="M134 595.71L18.7402 480.45L249.26 481.18L134 595.71Z"
        fill="url(#ctt_paint6)"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Render pawns at all 19 positions - from top to bottom for 3D overlap effect */}
      {renderMultiplePositions(
        RENDER_ORDER,
        pawns,
        CENTER_SQUARE_BOUNDS,
        undefined,
        1.0,
        animatingPawnIds,
        onPawnClick,
        clickablePawnIds,
        rollbackPawnIds,
        kickingPawnIds,
        pawnComponents
      )}

      <defs>
        {/* paint0: Blue gradient for Row 6 center (top to bottom) */}
        <linearGradient
          id="ctt_paint0"
          x1="134"
          y1="480.45"
          x2="134"
          y2="403.61"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#5FACD6" />
          <stop offset="0.48" stopColor="#4584C7" />
          <stop offset="1" stopColor="#3B57A1" />
        </linearGradient>

        {/* paint1: Dark gradient for Row 5 center (horizontal) */}
        <linearGradient
          id="ctt_paint1"
          x1="95.5801"
          y1="365.19"
          x2="172.42"
          y2="365.19"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#333333" />
          <stop offset="1" stopColor="#1A1A1A" />
        </linearGradient>

        {/* paint2: Blue gradient for Row 4 center (top to bottom) */}
        <linearGradient
          id="ctt_paint2"
          x1="4188.08"
          y1="326.77"
          x2="4188.08"
          y2="249.93"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#5FACD6" />
          <stop offset="0.48" stopColor="#4584C7" />
          <stop offset="1" stopColor="#3B57A1" />
        </linearGradient>

        {/* paint3: Dark gradient for Row 3 center */}
        <linearGradient
          id="ctt_paint3"
          x1="7.62939e-05"
          y1="211.5"
          x2="1.00008"
          y2="211.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#333333" />
          <stop offset="1" stopColor="#1A1A1A" />
        </linearGradient>

        {/* paint4: Blue gradient for Row 2 center (top to bottom) */}
        <linearGradient
          id="ctt_paint4"
          x1="134.01"
          y1="173.08"
          x2="134.01"
          y2="96.2401"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#5FACD6" />
          <stop offset="0.48" stopColor="#4584C7" />
          <stop offset="1" stopColor="#3B57A1" />
        </linearGradient>

        {/* paint5: Dark gradient for Row 2 right column */}
        <linearGradient
          id="ctt_paint5"
          x1="172.42"
          y1="134.66"
          x2="249.26"
          y2="134.66"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#333333" />
          <stop offset="1" stopColor="#1A1A1A" />
        </linearGradient>

        {/* paint6: Blue gradient for bottom triangle */}
        <linearGradient
          id="ctt_paint6"
          x1="18.7402"
          y1="538.08"
          x2="249.26"
          y2="538.08"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#5FACD6" />
          <stop offset="0.48" stopColor="#4584C7" />
          <stop offset="1" stopColor="#3B57A1" />
        </linearGradient>
      </defs>
    </svg>
  );
}
